import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { JobindsatsElement } from '../core/jobindsats-element';
import { DimensionOption } from './model/dimension-option-type';
import { dimensionStyles } from './styles/dimension-styles';
import { repeat } from 'lit/directives/repeat.js';

@customElement('dimension-manager')
export class DimensionManager extends JobindsatsElement(LitElement) {
    static styles = [dimensionStyles, css`
        #_rako, #_reko, #_rejo {
            column-count: 5;  /* Adjust based on the number of desired columns */
            column-gap: 20px; /* Space between columns */
            list-style-type: none;
            padding: 0;
            margin: 0;
            word-break: normal;
        }
        #_rako li, #_reko li, #_rejo li {
            break-inside: avoid; /* Prevents items from breaking across columns */
            display: block; /* Ensure each list item takes full width in its column */
            margin-bottom: 5px; /* Adds space between items */
        }
        ul {
            column-count: auto;
            column-width: 33vh;
        }
        li {
            page-break-inside: avoid;
            break-inside: avoid;
            display:inline-block;
            width: 100%;
        }
        
    `];


    @property({ type: Array }) groups: DimensionOption[] = [];
    @property({ type: Boolean }) collapsible: boolean = false;
    @property() groupNotReturnedAsNull = false;
    @property({ type: String }) selectedPeriod = "";

    @property({ type: String }) activeTab: string = '';


    static listIdCounter = 0;

    private listId: number;

    constructor() {
        super();
        this.listId = DimensionManager.listIdCounter++;
    }

    render() {

        if (this.groups) {
            // Separate "Hele landet" from the list
            const heleLandetOption = this.groups.find(option => option.name === "Hele landet");
            const filteredGroups = this.groups.filter(option => option.name !== "Hele landet");


            return html`
                <div class="selectors" style="display: flex; gap: 20px; align-items: center;">
                    <div class="select-all">
                        <a href="#!" role="button" @click=${this.handleAllSelected}>
                            Vælg &#47; Fravælg alle
                        </a>
                    </div>

                    <!-- Only render these buttons inside class="area-border" -->
                    ${this.groupNotReturnedAsNull && (this.activeTab === '_reko' || this.activeTab === '_rako') ? html`
                        <div class="select-all">
                            <a href="#!" role="button" @click=${() => this.handleAllSelectedKommuneOrRar(3)}>
                                Vælg &#47; Fravælg alle kommuner
                            </a>
                        </div>

                        <div class="select-all">
                            <a href="#!" role="button" @click=${() => this.handleAllSelectedKommuneOrRar(2)}>
                                Vælg &#47; Fravælg alle regioner
                            </a>
                        </div>
                    ` : ''}
                </div>

                <!-- Render "Hele landet" separately -->
                ${heleLandetOption ? html`
                    <a style="margin-bottom: 10px; display: inline-block; width: 100%;">
                        <collapsible-dimension-group-element 
                            @option-selected=${this.handleInnerOptionSelected} 
                            @group-selected=${this.handleGroupSelected} 
                            .group=${heleLandetOption}>
                        </collapsible-dimension-group-element>
                    </a>
                ` : ''}

                         <ul id="${this.selectedPeriod}" class=${this.collapsible ? 'collapsible-container' : ''} style=${this.groupNotReturnedAsNull ? 'column-width: 20vh;' : ''}>
                ${repeat(filteredGroups,
                (option) => option.id,
                (option) => {
                    if (option.children && option.children.length > 0) {
                        if (this.collapsible) {
                            return html`
                                    <li class="group-margin">
                                        <collapsible-dimension-group-element @option-selected=${this.handleInnerOptionSelected} @group-selected=${this.handleGroupSelected} .group=${option}></collapsible-dimension-group-element>
                                    </li>
                                `;
                        } else {
                            return html`
                                    <li class="group-margin">
                                        <dimension-group-element @option-selected=${this.handleInnerOptionSelected} @group-selected=${this.handleGroupSelected} .group=${option}></dimension-group-element>
                                    </li>
                                `;
                        }

                    } else {
                        return html`<li><dimension-group-element @option-selected=${this.handleInnerOptionSelected} .group=${option}></dimension-group-element></li>`;
                    }
                })}
                </ul>
            `;
        }
    }

    countCharacterOccurrences(input: string, character: string): number {
        return input.split(character).length - 1;
    }

    handleAllSelectedKommuneOrRar(numberOfSlashes: number) {
        if (this.groups) {
            let selectionMode = true;
            const targetKommuner = this.groups
                .flatMap(group => [group, ...(group.children ?? [])])
                .filter(item => this.countCharacterOccurrences(item.id, "/") === numberOfSlashes);

            if (targetKommuner.some(item => item.selected)) {
                selectionMode = false;
            }

            this.groups = this.groups.map(group => ({
                ...group,
                selected: targetKommuner.includes(group) ? selectionMode : group.selected,
                children: group.children?.map(child =>
                    targetKommuner.includes(child) ? { ...child, selected: selectionMode } : child
                )
            }));

            this.requestUpdate();
            this.emitOptionsChangedEvent();
        }
    }

    handleInnerOptionSelected(ev: CustomEvent) {
        const key: string = ev.detail.key;
        if (this.groups) {
            this.groups = this.groups.map(group => this.selectOptionInChild(group, key));
            this.emitOptionsChangedEvent();
        }
    }

    handleGroupSelected(ev: CustomEvent) {
        const key: string = ev.detail.key;
        if (this.groups) {
            this.groups = this.groups.map(child => this.selectGroup(child, key));
            this.emitOptionsChangedEvent();
        }
    }

    emitOptionsChangedEvent() {
        const optionSelectedEvent = new CustomEvent('options-selected', {
            detail: {
                options: this.groups.map(group => this.groupHasAnySelected(group)).some(a => a) || this.groupNotReturnedAsNull ? this.groups : null,
            },
            composed: true,
            bubbles: true,
        });
        this.dispatchEvent(optionSelectedEvent);
    }

    groupHasAnySelected(group: DimensionOption): boolean {
        if (group.selected) {
            return true;
        }
        return group.children?.some(child => this.groupHasAnySelected(child)) ?? false;
    }

    selectGroup(group: DimensionOption, key: string, selectionMode?: boolean): DimensionOption {
        if (group.id === key) {
            let newSelectionMode = selectionMode ?? !this.groupHasAnySelected(group);
            return {
                ...group,
                selected: newSelectionMode,
                children: group.children ? this.selectAllChildren(group.children, newSelectionMode) : undefined,
            };
        }
        return {
            ...group,
            children: group.children?.map(child => this.selectGroup(child, key, selectionMode))
        };
    }

    selectOptionInChild(group: DimensionOption, key: string): DimensionOption {
        if (group.id === key) {
            return { ...group, selected: !group.selected };
        }
        return {
            ...group,
            children: group.children?.map(child => this.selectOptionInChild(child, key))
        };
    }

    handleAllSelected() {
        if (this.groups) {
            let selectionMode = !this.groups.some(group => this.groupHasAnySelected(group));
            this.groups = this.selectAllChildren(this.groups, selectionMode);
            this.emitOptionsChangedEvent();
        }
    }

    selectAllChildren(children: DimensionOption[], selectionMode: boolean): DimensionOption[] {
        return children.map(child => ({
            ...child,
            selected: selectionMode,
            children: child.children ? this.selectAllChildren(child.children, selectionMode) : undefined
        }));
    }
}
